import cx from 'classnames'
import { Pressable } from '@react-aria/interactions'

import Styles from './ChooseTimeOfDay.module.scss'
import PostFrequencyStyles from '../PostFrequency/PostFrequency.module.scss'

import { Text } from 'blaze-ui/Text/Text'
import { DayAndTime, isToday, TimeOfDay, timeOfDayToDisplay } from '../../StartDateAndPostFrequencyModal'
import { useState } from 'react'
import { Check2, ChevronDown } from 'blaze-ui/icons'
import { MenuTrigger } from 'components/MenuTrigger/MenuTrigger'
import { Button } from 'blaze-ui/Button'
import { Menu } from 'components/Menu/Menu'
import dayjs, { Dayjs } from 'dayjs'
import { Item } from '@react-stately/collections'
import { Drawer } from 'components/Drawer'

interface Props {
  startDate: Dayjs
  initialDays: DayAndTime[]
  initialTimeOfDay: TimeOfDay
  initialCustomTime: string | undefined
  initialAmOrPm: string
  onConfirm: (days: DayAndTime[], customTime: string | undefined, customAmOrPm: string, timeOfDay: TimeOfDay) => void
}

export const ChooseTimeOfDay = ({
  initialDays,
  initialTimeOfDay,
  initialCustomTime,
  initialAmOrPm,
  startDate,
  onConfirm,
}: Props) => {
  const [days, setDays] = useState(initialDays)
  const [timeOfDay, setTimeOfDay] = useState(initialTimeOfDay)
  const [customTime, setCustomTime] = useState<string | undefined>(initialCustomTime)
  const [customAmOrPm, setCustomAmOrPm] = useState(initialAmOrPm)

  const handleConfirm = () => {
    onConfirm(days, customTime, customAmOrPm, timeOfDay)
  }

  return (
    <>
      <Drawer.Container>
        <div className={Styles.container}>
          <Text variant="placeholder" color="var(--dark-80)">
            Select when you would like to post
          </Text>
          <div className={PostFrequencyStyles.cadencesContainer}>
            {Object.entries(TimeOfDay).map(([_key, value], index) => {
              const timeOfDayDisplay = timeOfDayToDisplay(value)

              if (value !== TimeOfDay.Custom) {
                return (
                  <Pressable
                    key={value}
                    onPress={() => {
                      setTimeOfDay(value)
                      const newDays = days.map((day) => {
                        return { ...day, time: { ofDay: value } }
                      })
                      setDays(newDays)
                    }}
                  >
                    <div
                      className={cx(PostFrequencyStyles.cadenceContainer, {
                        [PostFrequencyStyles.topBorderRadius]: index === 0,
                      })}
                    >
                      <Text variant="largeList">{timeOfDayDisplay.text}</Text>
                      <div className={PostFrequencyStyles.cadenceDaysContainer}>
                        <span className={PostFrequencyStyles.cadenceDays}>{timeOfDayDisplay.time}</span>
                        {timeOfDay === value && <Check2 size={16} color="var(--selected-stroke)" />}
                      </div>
                    </div>
                  </Pressable>
                )
              }

              return (
                <Pressable
                  key={value}
                  onPress={() => {
                    setTimeOfDay(value)
                    const newDays = days.map((day) => {
                      return { ...day, time: { ofDay: value } }
                    })
                    setDays(newDays)
                  }}
                >
                  <div
                    className={cx(
                      Styles.customCadenceContainer,
                      PostFrequencyStyles.bottomBorder,
                      PostFrequencyStyles.bottomBorderRadius,
                    )}
                  >
                    <div className={Styles.titleAndCheck}>
                      <Text variant="largeList">{timeOfDayDisplay.text}</Text>
                      {timeOfDay === value && <Check2 size={16} color="var(--selected-stroke)" />}
                    </div>
                    <div>
                      {timeOfDay === value && value === TimeOfDay.Custom && (
                        <div className={Styles.customControls}>
                          <MenuTrigger>
                            <Button
                              variant="tertiary"
                              size="lg"
                              endIcon={ChevronDown}
                              color="var(--dark-80)"
                              onPress={() => {
                                if (timeOfDay !== TimeOfDay.Custom) setTimeOfDay(TimeOfDay.Custom)
                              }}
                            >
                              {customTime || 'Pick time'}
                            </Button>
                            <Menu
                              selectionMode="single"
                              selectedKeys={[customTime as string]}
                              onAction={(key) => {
                                setCustomTime(key as string)
                                const [hour, minute] = (key as string).split(':')
                                const newDays = days.map((oldDay) => {
                                  return {
                                    ...oldDay,
                                    time: {
                                      ...oldDay.time,
                                      at: {
                                        hour: parseInt(hour),
                                        minute: parseInt(minute),
                                        amOrPm: customAmOrPm,
                                      },
                                    },
                                  }
                                })
                                setDays(newDays as DayAndTime[])
                              }}
                            >
                              {[...Array(12)].map((num, index) =>
                                [0, 15, 30, 45].map((minute) => {
                                  if (isToday(startDate)) {
                                    let hour = index

                                    if (customAmOrPm === 'pm') {
                                      hour = index + 12
                                    }

                                    if (dayjs().hour() > hour) return null
                                    if (dayjs().hour() === hour && dayjs().minute() > minute) return null
                                  }

                                  const time = `${index === 0 ? 12 : index}:${minute === 0 ? '00' : minute}`
                                  return (
                                    <Item key={time} textValue={time}>
                                      {time}
                                    </Item>
                                  )
                                }),
                              )}
                            </Menu>
                          </MenuTrigger>
                          <MenuTrigger>
                            <Button
                              variant="tertiary"
                              size="lg"
                              endIcon={ChevronDown}
                              color="var(--dark-80)"
                              onPress={() => {
                                if (timeOfDay !== TimeOfDay.Custom) setTimeOfDay(TimeOfDay.Custom)
                              }}
                            >
                              {customAmOrPm.toUpperCase()}
                            </Button>
                            <Menu
                              selectionMode="single"
                              disabledKeys={isToday(startDate) && dayjs().hour() >= 12 ? ['am'] : []}
                              selectedKeys={[customAmOrPm]}
                              onAction={(key) => {
                                setCustomAmOrPm(key as string)
                                const newDays = days.map((day) => {
                                  return { ...day, time: { ...day.time, at: { ...day.time.at, amOrPm: key } } }
                                })
                                setDays(newDays as DayAndTime[])
                              }}
                            >
                              <Item key="am">AM</Item>
                              <Item key="pm">PM</Item>
                            </Menu>
                          </MenuTrigger>
                        </div>
                      )}
                    </div>
                  </div>
                </Pressable>
              )
            })}
          </div>
          <Button size="lg" onPress={handleConfirm} isDisabled={timeOfDay === TimeOfDay.Custom && !customTime}>
            Confirm
          </Button>
        </div>
      </Drawer.Container>
    </>
  )
}
